import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import axios from "axios";
import { RootState } from "../../app/store";

export interface AdminOffer {
  _id: string;

  user: string;
  createdBy?: string | null;
  isAssigned: boolean;

  shortName?: string;
  name: string;
  website?: string;
  email: string;
  phone: number;
  NIP: string;
  description: string;

  mainField: string[];

  coordinates: number[];

  city: string;
  street: string;
  streetNumber: string;
  localNumber?: string | null;
  zipCode: string;
  voivodeship: string;

  isInternational: boolean;
  isADR: boolean;
  isOversized: boolean;

  isActive: boolean;

  highlightedExpirationDate: string | null;
  addHighlighted: boolean;

  recommendedExpirationDate: string | null;
  addRecommended: boolean;

  addDate: string | null;
  expirationDate: string | null;
  period: number | null;

  addNewPeriod: boolean;
  newPeriod: number | null;

  newIsHighlighted: boolean;
  newIsRecommended: boolean;

  price: number;
  hasBills: boolean;
  isDeleted?: boolean;
  deletedAt?: string | null;
  benefits?: {
    type: "period" | "highlighted" | "recommended";
    value: number;
    unit: "years" | "months";
    source: "registration_free" | "admin_grant";
    grantedAt: string;
    grantedBy: string | null;
    note: string | null;
  }[];
}

interface AdminOfferState {
  offers: AdminOffer[];
  offer: AdminOffer | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminOfferState = {
  offers: [],
  offer: null,
  loading: false,
  error: null,
};

export const getAdminOffers = createAsyncThunk<
  AdminOffer[],
  void,
  { rejectValue: string }
>(
  "adminOffers/getAdminOffers",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        "/api/admin/offers",
        {
          headers: {
            ...(token && {
              "x-auth-token": token,
            }),
          },
        }
      );

      return response.data;
    } catch (err) {
      const error = err as any;

      console.error(
        "GET ADMIN OFFERS ERROR:",
        error.response?.data || error
      );

      return rejectWithValue(
        error.response?.data?.msg ||
          "Błąd podczas pobierania wizytówek"
      );
    }
  }
);

export const getAdminOfferById =
  createAsyncThunk<
    AdminOffer,
    string,
    { rejectValue: string }
  >(
    "adminOffers/getAdminOfferById",
    async (id, { rejectWithValue }) => {
      try {
        const token =
          localStorage.getItem("token");

        const response = await axios.get(
          `/api/admin/offers/${id}`,
          {
            headers: {
              ...(token && {
                "x-auth-token": token,
              }),
            },
          }
        );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "GET ADMIN OFFER ERROR:",
          error.response?.data || error
        );

        return rejectWithValue(
          error.response?.data?.msg ||
            "Błąd podczas pobierania wizytówki"
        );
      }
    }
  );

  export const updateAdminOfferOwner =
  createAsyncThunk<
    AdminOffer,
    {
      id: string;
      user: string;
    },
    { rejectValue: string }
  >(
    "adminOffers/updateAdminOfferOwner",
    async (
      { id, user },
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response = await axios.patch(
          `/api/admin/offers/${id}/owner`,
          {
            user,
          },
          {
            headers: {
              ...(token && {
                "x-auth-token": token,
              }),
            },
          }
        );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "UPDATE ADMIN OFFER OWNER ERROR:",
          error.response?.data || error
        );

        return rejectWithValue(
          error.response?.data?.msg ||
            "Nie udało się zmienić użytkownika wizytówki"
        );
      }
    }
  );

  export const updateAdminOfferCompanyData = createAsyncThunk(
    "adminOffers/updateCompanyData",
    async (
      {
        id,
        name,
        shortName,
        email,
        phone,
        website,
      }: {
        id: string;
        name: string;
        shortName: string;
        email: string;
        phone: string;
        website: string;
      },
      { rejectWithValue }
    ) => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.patch(
          `/api/admin/offers/${id}/company-data`,
          {
            name,
            shortName,
            email,
            phone,
            website,
          },
          {
            headers: {
              ...(token && {
                "x-auth-token": token,
              }),
            },
          }
        );

        return response.data.offer;
      } catch (err) {
        const error = err as any;
        return rejectWithValue(
          error.response?.data?.message ||
            "Nie udało się zaktualizować danych firmy"
        );
      }
    }
  );
  export const updateAdminOfferServices =
    createAsyncThunk(
      "adminOffers/updateServices",
      async (
        {
          id,
          mainField,
          isADR,
          isInternational,
          isOversized,
        }: {
          id: string;
          mainField: string[];
          isADR: boolean;
          isInternational: boolean;
          isOversized: boolean;
        },
        { rejectWithValue }
      ) => {
        try {
          const response = await axios.patch(
            `/api/admin/offers/${id}/services`,
            {
              mainField,
              isADR,
              isInternational,
              isOversized,
            }
          );

          return response.data.offer;
        } catch (err) {
          const error = err as any;

          return rejectWithValue(
            error.response?.data?.message ||
              "Nie udało się zaktualizować usług"
          );
        }
      }
    );

  export const updateAdminOfferDescription =
    createAsyncThunk(
      "adminOffers/updateDescription",
      async (
        {
          id,
          description,
        }: {
          id: string;
          description: string;
        },
        { rejectWithValue }
      ) => {
        try {
          const response = await axios.patch(
            `/api/admin/offers/${id}/description`,
            {
              description,
            }
          );

          return response.data.offer;
        } catch (err) {
          const error = err as any;

          return rejectWithValue(
            error.response?.data?.message ||
              "Nie udało się zaktualizować opisu"
          );
        }
      }
    );

  export interface UpdateAdminOfferAddressPayload {
  id: string;

  street: string;
  streetNumber: string;
  localNumber?: string | null;

  zipCode: string;
  city: string;
  voivodeship: string;
}

export const updateAdminOfferAddress =
  createAsyncThunk<
    AdminOffer,
    UpdateAdminOfferAddressPayload,
    { rejectValue: string }
  >(
    "adminOffers/updateAdminOfferAddress",
    async (
      {
        id,
        street,
        streetNumber,
        localNumber,
        zipCode,
        city,
        voivodeship,
      },
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response = await axios.patch(
          `/api/admin/offers/${id}/address`,
          {
            street,
            streetNumber,
            localNumber:
              localNumber || null,
            zipCode,
            city,
            voivodeship,
          },
          {
            headers: {
              ...(token && {
                "x-auth-token": token,
              }),
            },
          }
        );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "UPDATE ADMIN OFFER ADDRESS ERROR:",
          error.response?.data || error
        );

        return rejectWithValue(
          error.response?.data?.msg ||
            "Nie udało się zmienić adresu wizytówki"
        );
      }
    }
  );

  export interface ActivateAdminOfferResponse {
  success: boolean;
  message: string;
  offer: AdminOffer;
}

export const activateAdminOffer =
  createAsyncThunk<
    ActivateAdminOfferResponse,
    string,
    { rejectValue: string }
  >(
    "adminOffers/activateAdminOffer",
    async (
      id,
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response =
          await axios.patch(
            `/api/admin/offers/${id}/activate`,
            {},
            {
              headers: {
                ...(token && {
                  "x-auth-token":
                    token,
                }),
              },
            }
          );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "ACTIVATE ADMIN OFFER ERROR:",
          error.response?.data ||
            error
        );

        return rejectWithValue(
          error.response?.data?.error ||
            error.response?.data?.msg ||
            "Nie udało się aktywować wizytówki"
        );
      }
    }
  );

  export const deactivateAdminOffer =
  createAsyncThunk<
    ActivateAdminOfferResponse,
    string,
    { rejectValue: string }
  >(
    "adminOffers/deactivateAdminOffer",
    async (
      id,
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response =
          await axios.patch(
            `/api/admin/offers/${id}/deactivate`,
            {},
            {
              headers: {
                ...(token && {
                  "x-auth-token":
                    token,
                }),
              },
            }
          );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "DEACTIVATE ADMIN OFFER ERROR:",
          error.response?.data ||
            error
        );

        return rejectWithValue(
          error.response?.data?.error ||
            error.response?.data?.msg ||
            "Nie udało się dezaktywować wizytówki"
        );
      }
    }
  );

  export const activateAdminOfferFreePeriod =
  createAsyncThunk<
    ActivateAdminOfferResponse,
    string,
    { rejectValue: string }
  >(
    "adminOffers/activateAdminOfferFreePeriod",
    async (
      id,
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response =
          await axios.patch(
            `/api/admin/offers/${id}/activate-free-period`,
            {},
            {
              headers: {
                ...(token && {
                  "x-auth-token":
                    token,
                }),
              },
            }
          );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "ACTIVATE FREE PERIOD ERROR:",
          error.response?.data ||
            error
        );

        return rejectWithValue(
          error.response?.data?.error ||
            error.response?.data?.msg ||
            "Nie udało się aktywować darmowego okresu"
        );
      }
    }
  );

  export interface GrantAdminOfferBenefitData {
  periodYears: number;
  highlightedMonths: number;
  recommendedMonths: number;
  note?: string;
}

export const grantAdminOfferBenefit =
  createAsyncThunk<
    ActivateAdminOfferResponse,
    {
      id: string;
      data: GrantAdminOfferBenefitData;
    },
    { rejectValue: string }
  >(
    "adminOffers/grantAdminOfferBenefit",
    async (
      { id, data },
      { rejectWithValue }
    ) => {
      try {
        const token =
          localStorage.getItem("token");

        const response =
          await axios.patch(
            `/api/admin/offers/${id}/grant-benefit`,
            data,
            {
              headers: {
                ...(token && {
                  "x-auth-token":
                    token,
                }),
              },
            }
          );

        return response.data;
      } catch (err) {
        const error = err as any;

        console.error(
          "GRANT ADMIN OFFER BENEFIT ERROR:",
          error.response?.data ||
            error
        );

        return rejectWithValue(
          error.response?.data?.error ||
            error.response?.data?.msg ||
            "Nie udało się przyznać benefitu"
        );
      }
    }
  );

const adminOfferSlice = createSlice({
  name: "adminOffers",

  initialState,

  reducers: {
    clearAdminOfferError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAdminOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getAdminOffers.fulfilled,
        (
          state,
          action: PayloadAction<AdminOffer[]>
        ) => {
          state.loading = false;
          state.offers = action.payload;
        }
      )
      .addCase(
        getAdminOffers.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload ||
            "Nie udało się pobrać wizytówek";
        }
      )
      .addCase(
        getAdminOfferById.pending,
        (state) => {
          state.loading = true;
          state.error = null;
          state.offer = null;
        }
      )
      .addCase(
        getAdminOfferById.fulfilled,
        (
          state,
          action: PayloadAction<AdminOffer>
        ) => {
          state.loading = false;
          state.offer = action.payload;
        }
      )
      .addCase(
        getAdminOfferById.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Nie udało się pobrać wizytówki";
        }
      )
      .addCase(
        updateAdminOfferOwner.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addCase(
        updateAdminOfferOwner.fulfilled,
        (
          state,
          action: PayloadAction<AdminOffer>
        ) => {
          state.loading = false;
          state.offer = action.payload;

          const index = state.offers.findIndex(
            (offer) =>
              offer._id === action.payload._id
          );

          if (index !== -1) {
            state.offers[index] = action.payload;
          }
        }
      )
      .addCase(
        updateAdminOfferOwner.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Nie udało się zmienić użytkownika wizytówki";
        }
      )
      .addCase(
        updateAdminOfferCompanyData.fulfilled,
        (state, action) => {
          state.offer = action.payload;

          const index = state.offers.findIndex(
            (item) => item._id === action.payload._id
          );

          if (index !== -1) {
            state.offers[index] = action.payload;
          }
        }
      )
      .addCase(
        updateAdminOfferServices.fulfilled,
        (state, action) => {
          state.offer = action.payload;

          const index = state.offers.findIndex(
            (offer) =>
              offer._id === action.payload._id
          );

          if (index !== -1) {
            state.offers[index] =
              action.payload;
          }
        }
      )
      .addCase(
        updateAdminOfferDescription.fulfilled,
        (state, action) => {
          state.offer = action.payload;

          const index = state.offers.findIndex(
            (offer) =>
              offer._id === action.payload._id
          );

          if (index !== -1) {
            state.offers[index] =
              action.payload;
          }
        }
      )
      .addCase(
        updateAdminOfferAddress.pending,
        (state) => {
          state.error = null;
        }
      )
      .addCase(
        updateAdminOfferAddress.fulfilled,
        (
          state,
          action: PayloadAction<AdminOffer>
        ) => {
          state.offer = action.payload;

          const index = state.offers.findIndex(
            (offer) =>
              offer._id === action.payload._id
          );

          if (index !== -1) {
            state.offers[index] =
              action.payload;
          }
        }
      )
      .addCase(
        updateAdminOfferAddress.rejected,
        () => {
          // błąd adresu obsługujemy lokalnie
          // w OfferDetails przez addressError
        }
      )
      .addCase(
        activateAdminOffer.fulfilled,
        (state, action) => {
          state.offer =
            action.payload.offer;

          const index =
            state.offers.findIndex(
              (offer) =>
                offer._id ===
                action.payload.offer._id
            );

          if (index !== -1) {
            state.offers[index] =
              action.payload.offer;
          }
        }
      )
      .addCase(
        deactivateAdminOffer.fulfilled,
        (state, action) => {
          state.offer =
            action.payload.offer;

          const index =
            state.offers.findIndex(
              (offer) =>
                offer._id ===
                action.payload.offer._id
            );

          if (index !== -1) {
            state.offers[index] =
              action.payload.offer;
          }
        }
      )
      .addCase(
        activateAdminOfferFreePeriod.fulfilled,
        (state, action) => {
          state.offer =
            action.payload.offer;

          const index =
            state.offers.findIndex(
              (offer) =>
                offer._id ===
                action.payload.offer._id
            );

          if (index !== -1) {
            state.offers[index] =
              action.payload.offer;
          }
        }
      )
      .addCase(
        grantAdminOfferBenefit.fulfilled,
        (state, action) => {
          state.offer =
            action.payload.offer;

          const index =
            state.offers.findIndex(
              (offer) =>
                offer._id ===
                action.payload.offer._id
            );

          if (index !== -1) {
            state.offers[index] =
              action.payload.offer;
          }
        }
      )
  },
});

export const { clearAdminOfferError } =
  adminOfferSlice.actions;

export const selectAdminOffers = (
  state: RootState
) => state.adminOffer.offers;

export const selectAdminOffersLoading = (
  state: RootState
) => state.adminOffer.loading;

export const selectAdminOffersError = (
  state: RootState
) => state.adminOffer.error;

export const selectAdminOffer = (
  state: RootState
) => state.adminOffer.offer;

export default adminOfferSlice.reducer;